import React, { Suspense } from 'react';
import PaymentErrorBoundary from './PaymentErrorBoundary';

// Lazy-load the Square SDK so its 'Failed to fetch' / network errors at
// module init do NOT crash the whole page on load. The SDK is only loaded
// the first time a payment dialog opens.
const LazyPaymentForm = React.lazy(() =>
  import('react-square-web-payments-sdk').then((mod) => ({ default: mod.PaymentForm }))
);
const LazyCreditCard = React.lazy(() =>
  import('react-square-web-payments-sdk').then((mod) => ({ default: mod.CreditCard }))
);

/**
 * Safe wrapper around Square's PaymentForm.
 * - Lazy-loads the SDK to avoid module-init network failures crashing the app.
 * - Catches runtime errors with PaymentErrorBoundary.
 * - Shows a friendly fallback while the SDK is loading.
 */
export const SafePaymentForm = ({
  applicationId,
  locationId,
  cardTokenizeResponseReceived,
  createPaymentRequest,
  buttonText,
  buttonCss,
  processing,
}) => {
  if (!applicationId || !locationId) {
    return (
      <div className="bg-red-50 border border-red-200 p-4 text-sm text-red-700" data-testid="square-config-missing">
        <p className="font-bold mb-1">Configuration de paiement manquante</p>
        <p className="text-xs">Le module de paiement n'est pas configure. Veuillez contacter l'organisateur.</p>
      </div>
    );
  }

  return (
    <PaymentErrorBoundary>
      <Suspense
        fallback={
          <div className="bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600 text-center" data-testid="square-loading">
            <div className="inline-block w-4 h-4 border-2 border-slate-300 border-t-brand rounded-full animate-spin mr-2 align-middle" />
            Chargement du module de paiement securise...
          </div>
        }
      >
        <LazyPaymentForm
          applicationId={applicationId}
          locationId={locationId}
          cardTokenizeResponseReceived={cardTokenizeResponseReceived}
          createPaymentRequest={createPaymentRequest}
        >
          <LazyCreditCard
            buttonProps={{
              css: buttonCss || {
                backgroundColor: '#ff4500',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '700',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                padding: '0 16px',
                '&:hover': { backgroundColor: '#e03e00' },
              },
            }}
          >
            {processing ? 'Traitement...' : buttonText}
          </LazyCreditCard>
        </LazyPaymentForm>
      </Suspense>
    </PaymentErrorBoundary>
  );
};

export default SafePaymentForm;
